import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ContractAdvertiser } from '@core/models/contract-advertiser.model';
import { ContractCgvStatus } from '@core/models/contract-cgv-status.model';
import { ContractSector } from '@core/models/contract-sector.model';
import { ContractAdvertiserService } from '@core/services/contract-advertiser/contract-advertiser.service';
import { ContractService } from '@core/services/contract/contract.service';
import moment from 'moment/moment';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-contract-creating-dialog',
  templateUrl: './contract-creating-dialog.component.html',
  styleUrls: ['./contract-creating-dialog.component.scss'],
})
export class ContractCreatingDialogComponent implements OnInit, OnDestroy {
  public isSaving = false;
  public newContractForm: FormGroup;

  // Advertisers
  public isLoadingAdvertisers = false;
  public advertiserList: ContractAdvertiser[] = [];
  public filteredAdvertiserList: Observable<ContractAdvertiser[]>;

  // Years
  public isLoadingYears = false;
  public yearList: string[] = [
    moment().format('YYYY'),
    // moment().add(1, 'year').format('YYYY')
  ];

  // Contract Types
  public contractCgvTypeList = [
    { id: 1, name: 'Contrat juridique' },
    { id: 2, name: "Lettre d'engagement" },
  ];

  private componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private contractService: ContractService,
    private contractAdvertiserService: ContractAdvertiserService,
    public dialogRef: MatDialogRef<ContractCreatingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { year },
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getContractAdvertiser();
  }

  private initForm(): void {
    this.newContractForm = this.fb.group({
      advertiser: [null, Validators.required],
      contractCgvType: [null, Validators.required],
      year: [null, Validators.required],
    });
  }

  // --- Load autocomplete data ---

  // --- advertiser status ---
  private getContractAdvertiser() {
    this.isLoadingAdvertisers = true;

    this.contractAdvertiserService
      .getList({ year: this.data.year })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        advertiserList => {
          this.isLoadingAdvertisers = false;
          this.advertiserList = advertiserList;
          this.initFilteredAdvertiserList();
        },
        error => {
          this.isLoadingAdvertisers = false;
        }
      );
  }

  private initFilteredAdvertiserList() {
    if (!this.isLoadingAdvertisers) {
      this.filteredAdvertiserList = this.newContractForm.get('advertiser').valueChanges.pipe(
        debounceTime(500),
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterAdvertiser(label) : this.advertiserList.slice()))
      );
    }
  }

  private _filterAdvertiser(value: string = ''): ContractAdvertiser[] {
    const filterValue = value.toLowerCase();
    return this.advertiserList.filter(
      contractCgvStatus =>
        contractCgvStatus.id.toString().toLowerCase().includes(filterValue) || contractCgvStatus.name.toLowerCase().includes(filterValue)
    );
  }

  // Save Button

  public onValidationButtonClick() {
    this.isSaving = true;

    if (this.newContractForm.invalid) {
      this.newContractForm.markAllAsTouched();
      this.isSaving = false;
      this.displaySnackBar('Le formulaire est invalide. Veuillez vérifier les informations renseignées.', 'tv-chip-error');
      return;
    }

    // CREATE
    this.contractService.create(this.newContractForm.value).subscribe(
      result => {
        this.router.navigate(['/commercial/contract/' + this.newContractForm.get('year').value + '/' + result.advertiserId]).then(() => {});
        this.dialogRef.close();
      },
      error => {
        this.isSaving = false;
        this.displaySnackBar('Erreur lors de la création.', 'tv-chip-error');
      }
    );
  }

  public onCancelUpdateButtonClick() {
    this.displaySnackBar('La création du contrat a été annulée.', 'tv-chip-error');
    this.dialogRef.close();
  }

  // --- Utility ---

  private _getLabel(value: string | ContractCgvStatus | ContractSector): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  public displayAutoCompleteData(data: any): string {
    if (!data) return '';
    return typeof data === 'string' ? data : data.name + ' (' + data.id + ')';
  }

  public displaySnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, null, {
      duration: 4000,
      verticalPosition: 'top',
      panelClass: [panelClass],
    });
  }

  // --- On Destroy ---

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
